import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  withStyles
} from '@material-ui/core';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Clear from '@material-ui/icons/Clear';
import { styles } from './styles';
import ButtonIcon from 'components/button-icon';
import { Column, TableListPaginationProps } from './types';

const TableListPagination: React.FC<TableListPaginationProps> = ({
  classes,
  fetchedData,
  itemsPerPageOptions,
  page,
  perPage,
  columns,
  changePage,
  changeSize,
  showClearOrder,
  onClickClearOrder,
  sortedFields,
  onSort,
  customBlankState,
  intlText,
  clearOrderText = 'Clear Order',
  renderPageSize = true
}) => {
  const visibleColumns = useMemo(() => 
    columns.filter(column => column.visible),
    [columns]
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    changePage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    changeSize(parseInt(event.target.value, 10));
    changePage(1);
  };

  const handleSort = (attribute: string) => {
    onSort({ attribute });
  };

  const renderSortIcon = (attribute: string) => {
    const order = sortedFields.getOrder(attribute);
    if (!order) return null;
    
    return order === 'asc' ? 
      <ArrowUpward fontSize="small" /> : 
      <ArrowDownward fontSize="small" />;
  };

  const renderHeaderCell = (column: Column) => {
    const label = typeof column.labelHeader === 'function' ? 
      column.labelHeader() : 
      column.labelHeader;

    return (
      <TableCell
        key={column.attribute}
        align={column.align}
        className={`${classes.headerCell} ${column.sortable ? classes.sortableHeader : ''}`}
        style={{ width: column.width }}
        onClick={() => column.sortable && handleSort(column.attribute)}
      >
        <div className={classes.headerContent} style={{ justifyContent: column.align === 'center' ? 'center' : 'flex-start' }}>
          {label}
          {column.sortable && renderSortIcon(column.attribute)}
        </div>
      </TableCell>
    );
  };

  if (!fetchedData.data.length && customBlankState) {
    return <>{customBlankState}</>;
  }

  return (
    <>
      {showClearOrder && (
        <div className={classes.clearOrderContainer}>
          <ButtonIcon
            buttonIcon={<Clear />}
            onClick={onClickClearOrder}
            buttonText={intlText ? clearOrderText : 'Clear Order'}
            className={classes.clearOrderText}
            style={{ position: 'absolute', right: '16px', top: '135px' }}
          />
        </div>
      )}
      
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead className={classes.tableHead}>
            <TableRow>
              {visibleColumns.map(renderHeaderCell)}
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchedData.data.map((row) => (
              <TableRow key={row.Id}>
                {visibleColumns.map(column => (
                  <TableCell key={column.attribute} align={column.align}>
                    {column.renderCol(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {renderPageSize && <TablePagination
        component="div"
        count={fetchedData.totalCount}
        page={(page > 0 ? page - 1 : 0)}
        onPageChange={handleChangePage}
        rowsPerPage={perPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={itemsPerPageOptions}
      />}
    </>
  );
};

export default withStyles(styles)(TableListPagination);
